
import React, { useEffect } from 'react';
import { GenericScheduleProps, Schedule18ParticipationExemptionData, ParticipationExemptionItem, ParticipationIncomeLossItem, FormErrorsStep } from '../../types';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Checkbox } from '../ui/Checkbox';
import { RadioGroup } from '../ui/RadioGroup';
import { Button } from '../ui/Button';
import { safeParseFloat } from '../../utils/calculations';
import { translations } from '../../i18n/translations';

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h4 className="text-md font-semibold text-sky-700 mt-4 mb-2 border-b pb-1">{title}</h4>
);

const countryOptions = (t: GenericScheduleProps<Schedule18ParticipationExemptionData>['t']) => [
    { value: '', label: t('select_option') },
    { value: 'AE', label: t('country_ae') },
    { value: 'US', label: t('country_us') },
    { value: 'GB', label: t('country_gb') },
    { value: 'IN', label: t('country_in') },
    { value: 'SA', label: t('country_sa') },
    { value: 'EG', label: t('country_eg') },
];

const natureOfIncomeLossOptions = (t: GenericScheduleProps<Schedule18ParticipationExemptionData>['t']): {value: ParticipationIncomeLossItem['natureOfIncomeLoss'], label: string}[] => [
    { value: '', label: t('select_option') },
    { value: 'foreign_dividends', label: t('sch18_income_nature_foreign_dividends') },
    { value: 'capital_gains_losses', label: t('sch18_income_nature_capital_gains_losses') },
    { value: 'fx_gains_losses', label: t('sch18_income_nature_fx_gains_losses') },
    { value: 'impairment_gains_losses', label: t('sch18_income_nature_impairment_gains_losses') },
];


export const Schedule18_ParticipationExemption: React.FC<GenericScheduleProps<Schedule18ParticipationExemptionData>> = ({ scheduleData, onChange, errors, t, fullFormData }) => {
  
  const handleParticipationChange = (index: number, field: keyof ParticipationExemptionItem, value: any) => {
    const updatedParticipations = scheduleData.participations.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    );
    onChange('participations', updatedParticipations);
  };

  const addParticipation = () => {
    onChange('participations', [
      ...scheduleData.participations,
      { 
        id: Date.now().toString(), 
        name: '', 
        countryOfResidency: '', 
        trnOrTin: '', 
        subjectToTaxConfirmation: '', 
        subjectToTaxReason: '', 
        incomeLossItems: [{ id: Date.now().toString() + '_income', natureOfIncomeLoss: '', incomeToExempt: '', lossToExempt: '' }],
        ownershipPercentage: '',
        acquisitionCost: '',
        dateIncomeLossDerived: '',
        heldFor12MonthsConfirmation: '',
        intendToHoldFor12MonthsConfirmation: '',
        otherConditionsMetConfirmation: '',
      }
    ]);
  };

  const removeParticipation = (id: string) => {
    onChange('participations', scheduleData.participations.filter(p => p.id !== id));
  };

  const handleIncomeLossItemChange = (partIndex: number, itemIndex: number, field: keyof ParticipationIncomeLossItem, value: string) => {
    const updatedParticipations = scheduleData.participations.map((p, pIdx) => {
      if (pIdx === partIndex) {
        const updatedItems = p.incomeLossItems.map((item, iIdx) => 
          iIdx === itemIndex ? { ...item, [field]: value } : item
        );
        return { ...p, incomeLossItems: updatedItems };
      }
      return p;
    });
    onChange('participations', updatedParticipations);
  };

  const addIncomeLossItem = (partIndex: number) => {
    const updatedParticipations = scheduleData.participations.map((p, pIdx) => {
      if (pIdx === partIndex) {
        return { ...p, incomeLossItems: [...p.incomeLossItems, { id: Date.now().toString(), natureOfIncomeLoss: '', incomeToExempt: '', lossToExempt: '' }] };
      }
      return p;
    });
    onChange('participations', updatedParticipations);
  };

  const removeIncomeLossItem = (partIndex: number, itemId: string) => {
     const updatedParticipations = scheduleData.participations.map((p, pIdx) => {
      if (pIdx === partIndex) {
        return { ...p, incomeLossItems: p.incomeLossItems.filter(item => item.id !== itemId) };
      }
      return p;
    });
    onChange('participations', updatedParticipations);
  };

  useEffect(() => {
    let totalExempted = 0;
    scheduleData.participations.forEach(p => {
      p.incomeLossItems.forEach(item => {
        totalExempted += safeParseFloat(item.incomeToExempt) - safeParseFloat(item.lossToExempt);
      });
    });
    if (scheduleData.totalIncomeExempted !== totalExempted.toFixed(2)) {
      onChange('totalIncomeExempted', totalExempted.toFixed(2));
    }
  }, [scheduleData.participations, onChange, scheduleData.totalIncomeExempted]);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-sky-600">{t('sch18_title')}</h3>
      
      {scheduleData.participations.map((participation, partIndex) => (
        <div key={participation.id} className="p-4 border rounded-md space-y-3 bg-slate-50 relative">
            <div className="flex justify-between items-center">
                <p className="text-md font-semibold text-gray-700">{t('sch18_participation_heading', {index: partIndex + 1})}: {participation.name || t('sch18_new_participation')}</p>
                <Button onClick={() => removeParticipation(participation.id)} variant="danger_outline" size="sm">{t('remove_item_button')}</Button>
            </div>
            
            <Input label={t('sch18_participation_name_label')} type="text" id={`s18Name-${participation.id}`} value={participation.name} onChange={(e) => handleParticipationChange(partIndex, 'name', e.target.value)} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.name as string}/>
            <Select label={t('sch18_country_residency_label')} id={`s18Country-${participation.id}`} value={participation.countryOfResidency} options={countryOptions(t)} onChange={(e) => handleParticipationChange(partIndex, 'countryOfResidency', e.target.value)} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.countryOfResidency as string}/>
            <Input label={t('sch18_trn_tin_label')} type="text" id={`s18TrnTin-${participation.id}`} value={participation.trnOrTin} onChange={(e) => handleParticipationChange(partIndex, 'trnOrTin', e.target.value)} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.trnOrTin as string}/>
            <RadioGroup label={t('sch18_subject_to_tax_label')} name={`s18SubjectToTax-${participation.id}`} value={participation.subjectToTaxConfirmation} onChange={(val) => handleParticipationChange(partIndex, 'subjectToTaxConfirmation', val)} options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]} t={t} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.subjectToTaxConfirmation as string}/>
            {participation.subjectToTaxConfirmation === 'no' && (
                 <Input label={t('sch18_subject_to_tax_reason_label')} type="text" id={`s18SubjectToTaxReason-${participation.id}`} value={participation.subjectToTaxReason || ''} onChange={(e) => handleParticipationChange(partIndex, 'subjectToTaxReason', e.target.value)} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.subjectToTaxReason as string}/>
            )}

            <SectionHeader title={t('sch18_income_loss_items_title')} />
            {participation.incomeLossItems.map((item, itemIndex) => (
                <div key={item.id} className="p-3 border rounded-md space-y-2 bg-white relative">
                    <div className="flex justify-between items-center">
                        <p className="text-sm font-semibold text-gray-600">{t('sch18_income_loss_item_heading', {index: itemIndex + 1})}</p>
                        {participation.incomeLossItems.length > 1 && <Button onClick={() => removeIncomeLossItem(partIndex, item.id)} variant="danger_outline" size="sm">{t('remove_item_button')}</Button>}
                    </div>
                    <Select label={t('sch18_nature_income_loss_label')} id={`s18Nature-${item.id}`} value={item.natureOfIncomeLoss} options={natureOfIncomeLossOptions(t)} onChange={(e) => handleIncomeLossItemChange(partIndex, itemIndex, 'natureOfIncomeLoss', e.target.value as ParticipationIncomeLossItem['natureOfIncomeLoss'])} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.incomeLossItems?.[itemIndex]?.natureOfIncomeLoss as string}/>
                    <Input label={t('sch18_income_to_exempt_label')} type="number" id={`s18IncExempt-${item.id}`} value={item.incomeToExempt} onChange={(e) => handleIncomeLossItemChange(partIndex, itemIndex, 'incomeToExempt', e.target.value)} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.incomeLossItems?.[itemIndex]?.incomeToExempt as string}/>
                    <Input label={t('sch18_loss_to_exempt_label')} type="number" id={`s18LossExempt-${item.id}`} value={item.lossToExempt} onChange={(e) => handleIncomeLossItemChange(partIndex, itemIndex, 'lossToExempt', e.target.value)} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.incomeLossItems?.[itemIndex]?.lossToExempt as string} helpText={t('sch18_loss_to_exempt_help')}/>
                </div>
            ))}
            <Button onClick={() => addIncomeLossItem(partIndex)} variant="secondary" size="sm">{t('sch18_add_income_loss_item_button')}</Button>
            
            <Input label={t('sch18_ownership_percentage_label')} type="number" id={`s18OwnershipPerc-${participation.id}`} value={participation.ownershipPercentage} onChange={(e) => handleParticipationChange(partIndex, 'ownershipPercentage', e.target.value)} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.ownershipPercentage as string} min="0" max="100"/>
            {(safeParseFloat(participation.ownershipPercentage) < 5 && safeParseFloat(participation.ownershipPercentage) > 0) && (
                <Input label={t('sch18_acquisition_cost_label')} type="number" id={`s18AcqCost-${participation.id}`} value={participation.acquisitionCost} onChange={(e) => handleParticipationChange(partIndex, 'acquisitionCost', e.target.value)} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.acquisitionCost as string}/>
            )}
            <Input label={t('sch18_date_income_loss_derived_label')} type="date" id={`s18DateDerived-${participation.id}`} value={participation.dateIncomeLossDerived} onChange={(e) => handleParticipationChange(partIndex, 'dateIncomeLossDerived', e.target.value)} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.dateIncomeLossDerived as string}/>
            <RadioGroup label={t('sch18_held_12_months_label')} name={`s18Held12Months-${participation.id}`} value={participation.heldFor12MonthsConfirmation} onChange={(val) => handleParticipationChange(partIndex, 'heldFor12MonthsConfirmation', val)} options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]} t={t} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.heldFor12MonthsConfirmation as string}/>
            {participation.heldFor12MonthsConfirmation === 'no' && (
                <RadioGroup label={t('sch18_intend_hold_12_months_label')} name={`s18IntendHold12Months-${participation.id}`} value={participation.intendToHoldFor12MonthsConfirmation || ''} onChange={(val) => handleParticipationChange(partIndex, 'intendToHoldFor12MonthsConfirmation', val)} options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]} t={t} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.intendToHoldFor12MonthsConfirmation as string}/>
            )}
            <RadioGroup label={t('sch18_other_conditions_met_label')} name={`s18OtherCond-${participation.id}`} value={participation.otherConditionsMetConfirmation} onChange={(val) => handleParticipationChange(partIndex, 'otherConditionsMetConfirmation', val)} options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]} t={t} error={(errors?.participations as FormErrorsStep[])?.[partIndex]?.otherConditionsMetConfirmation as string}/>

        </div>
      ))}
      <Button onClick={addParticipation} variant="secondary" size="sm">{t('sch18_add_participation_button')}</Button>

      <Input label={t('sch18_total_exempted_label')} type="text" id="s18TotalExempted" value={scheduleData.totalIncomeExempted || '0.00'} readOnly className="bg-gray-100 font-semibold" />
    </div>
  );
};
